import { useMemo } from 'react';
import { Layer, Source } from 'shared/map-exports';
import { useTranslation } from 'react-i18next';
import { FeatureCollection, Feature, Geometry } from 'geojson';
import { WildfireListItem } from 'features/wildfires/wildfire.types';
import { getAcreageStrParts, truncateString } from 'shared/utils';
import { useMapLayersState } from 'state/useMapLayersState';
import { MapClickHandler, MapboxFeature } from 'features/mapping/mapping.types';
import {
  getOpacityForGeoEventMarker,
  getWildfireEventScale,
  getWildfireGeoEventIcon,
} from 'features/mapping/icons';
import { WILDFIRE_ICON_TO_SELECTED_NAME } from 'features/mapping/icons/wildfireIcons.utils';
import {
  ON_TOP_OF_EVERYTHING_LAYER_ID,
  WILDFIRE_SOURCE_ID,
} from 'features/mapping/styles/constants';
import { DEFAULT_LOCALE } from 'i18n/utils';
import { history } from 'shared/history';
import {
  COMPLEX_WILDFIRE_ICONS_LAYER_ID,
  COMPLEX_WILDFIRE_LABELS_LAYER_ID,
  COMPLEX_WILDFIRE_PARENT_CHIP_LAYER_ID,
  WILDFIRE_ICONS_LAYER_ID,
  WILDFIRE_LABELS_LAYER_ID,
} from './WildfiresLayer.constants';
import {
  getComplexChildIncidentChipStyle,
  getComplexIconStyle,
  getComplexLabelStyle,
  getIconStyle,
  getLabelStyle,
} from './WildfiresLayer.styles';

type WildfiresLayerProps = {
  wildfires: WildfireListItem[];
  isFadable?: boolean;
  selectedGeoEventId?: number;
  isEdit?: boolean;
  interactive?: boolean;
  emphasizedGeoEventIds?: Set<number>;
};

type GeoEventFeatureProperties = {
  id: number;
  name: string;
  icon: string;
  selectedIcon: string;
  isActive: boolean;
  isComplexParent: boolean;
  isComplexChild: boolean;
  complexParentName?: string;
  complexChildrenCount: number;
  isPrescribed: boolean;
  acreage: number | null;
  scale: number;
  complexChipOffsetX: number;
  complexChipOffsetY: number;
  offsetX: number;
  offsetY: number;
  opacity: number;
  index: number;
  indexInv: number;
  acreageStr: string;
  geoEventId: number;
};

const ONE_YEAR_MS = 31560000000;

const COMPLEX_PARENT_TRUNCATE_LENGTH = 25;

const getAcreageStr = (
  acreage: number | null,
  containment: number | null,
  locale: string,
): string => {
  const parts = getAcreageStrParts(acreage, containment, locale);
  if (parts[0] == null) return '';
  return parts.join(' ');
};

const getSortIndex = (geoEvent: WildfireListItem): number => {
  const {
    isActive,
    data: { isComplexParent, isPrescribed },
  } = geoEvent;

  if (!isActive || isPrescribed) {
    return 0;
  }

  if (geoEvent.notificationType === 'silent') {
    return 1;
  }

  const defaultSortIndex = geoEvent.dateCreated.getTime();

  if (isComplexParent) {
    return defaultSortIndex + ONE_YEAR_MS;
  }

  return defaultSortIndex;
};

const toGeojsonFeature = (
  wildfire: WildfireListItem,
  isFadable: boolean,
  locale: string,
  emphasizedGeoEventIds?: Set<number>,
): Feature<Geometry, GeoEventFeatureProperties> => {
  const {
    isActive,
    id,
    name,
    parentGeoEvents,
    childGeoEvents,
    data: { acreage, isComplexParent, containment },
  } = wildfire;
  const icon = getWildfireGeoEventIcon(wildfire);

  // If there are active filters, and this incident is not in the matching set,
  // we reduce the size and opacity of the icon.
  const isFilteredOut = emphasizedGeoEventIds && !emphasizedGeoEventIds.has(id);

  const geoEventScale = getWildfireEventScale(wildfire, isFilteredOut);
  const opacity = getOpacityForGeoEventMarker({
    geoEvent: wildfire,
    isFadable,
    isFilteredOut,
  });
  const acreageStr = getAcreageStr(acreage, containment, locale);

  // Sort indexes for labels and text. Inactive and Rx fires on the bottom,
  // then oldest to newest.
  const index = getSortIndex(wildfire);
  const indexInv = 1 / (index + 1);

  // Calculate scale and associated offset.
  const iconScale = icon.scale || 1.0;
  const scale = geoEventScale * iconScale;
  const offsetX = icon.offset ? icon.offset[0] * scale : 0;
  const offsetY = icon.offset ? icon.offset[1] * scale : 0;

  // We have to manually push complex chip labels down further since the complex icon is center-anchoed
  const complexChipOffsetX = offsetX;
  const complexChipOffsetY = offsetY * 1.9;

  const complexParentName = parentGeoEvents[0]
    ? truncateString(parentGeoEvents[0].name, COMPLEX_PARENT_TRUNCATE_LENGTH)
    : undefined;

  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [wildfire.lng, wildfire.lat],
    },
    properties: {
      id,
      name,
      icon: icon.name,
      selectedIcon: WILDFIRE_ICON_TO_SELECTED_NAME[icon.name] || icon.name,
      isActive,
      isComplexParent,
      isComplexChild: Boolean(complexParentName),
      acreage,
      scale,
      complexChipOffsetX,
      complexChipOffsetY,
      offsetX,
      offsetY,
      opacity,
      index,
      indexInv,
      acreageStr,
      geoEventId: id,
      complexParentName,
      complexChildrenCount: childGeoEvents.filter(
        (ge) => ge.geoEventType === 'wildfire',
      ).length,
      isPrescribed: wildfire.data.isPrescribed,
    },
  };
};

export const resolveWildfirePath = (
  geoJsonFeatures: MapboxFeature<GeoEventFeatureProperties>[],
): string | null => {
  // If there is ambiguity about which icon the user clicked on, select the
  // largest fire that has a higher index (based on activity/date created)
  const features = [...geoJsonFeatures];
  features.sort((a, b) => {
    if (
      a.properties.isActive === b.properties.isActive &&
      a.properties.acreage &&
      b.properties.acreage
    ) {
      return b.properties.acreage - a.properties.acreage;
    }
    return a.properties.isActive ? -1 : 1;
  });

  const feature = features[0];
  const { id, opacity } = feature.properties;
  if (opacity > 0) return `/i/${id}`;
  return null;
};

export const wildfiresClickHandler: MapClickHandler<
  GeoEventFeatureProperties
> = (geoJsonFeatures): void => {
  const path = resolveWildfirePath(geoJsonFeatures);
  if (path) history.push(path);
};

export const WildfiresLayer = (props: WildfiresLayerProps): JSX.Element => {
  const {
    wildfires,
    isFadable = false,
    selectedGeoEventId = 0,
    isEdit = false,
    interactive = true,
    emphasizedGeoEventIds,
  } = props;
  const { isLightBaseLayer } = useMapLayersState();

  const { i18n } = useTranslation();

  const geojson: FeatureCollection<Geometry, GeoEventFeatureProperties> =
    useMemo(
      () => ({
        type: 'FeatureCollection',
        features: wildfires.map((wildfire) => {
          return toGeojsonFeature(
            wildfire,
            isFadable,
            i18n.resolvedLanguage || DEFAULT_LOCALE,
            emphasizedGeoEventIds,
          );
        }),
      }),
      [wildfires, isFadable, i18n.resolvedLanguage, emphasizedGeoEventIds],
    );

  return (
    <Source id={WILDFIRE_SOURCE_ID} type="geojson" data={geojson}>
      <Layer
        id={WILDFIRE_ICONS_LAYER_ID}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...getIconStyle(selectedGeoEventId, isEdit)}
        metadata={{ interactive }}
      />
      <Layer
        id={WILDFIRE_LABELS_LAYER_ID}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...getLabelStyle(isLightBaseLayer, selectedGeoEventId)}
        metadata={{ interactive }}
      />
      <Layer
        id={COMPLEX_WILDFIRE_PARENT_CHIP_LAYER_ID}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...getComplexChildIncidentChipStyle(selectedGeoEventId)}
        metadata={{ interactive }}
      />
      <Layer
        id={COMPLEX_WILDFIRE_ICONS_LAYER_ID}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...getComplexIconStyle(selectedGeoEventId, isEdit)}
        metadata={{ interactive }}
      />
      <Layer
        id={COMPLEX_WILDFIRE_LABELS_LAYER_ID}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...getComplexLabelStyle(selectedGeoEventId)}
        metadata={{ interactive }}
      />
    </Source>
  );
};
